import React, { useState } from "react";
import { Modal, Button, Form, Alert, Spinner } from "react-bootstrap";
import api from "../services/api";
import { useLanguage } from "../i18n/LanguageContext";

interface TwoFADisableModalProps {
  show: boolean;
  onHide: () => void;
  onSuccess: () => void;
}

const TwoFADisableModal: React.FC<TwoFADisableModalProps> = ({ show, onHide, onSuccess }) => {
  const { t, dir } = useLanguage();
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleDisable = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await api.post('/2fa/disable', { password });
      onSuccess();
      handleClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to disable 2FA');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setPassword('');
    setError('');
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} centered dir={dir}>
      <Modal.Header closeButton>
        <Modal.Title>🔓 {t("disable2FA")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Alert variant="warning">
          <strong>⚠️</strong> {t("disable2FAWarning")}
        </Alert>

        <Form onSubmit={handleDisable}>
          <Form.Group className="mb-3">
            <Form.Label>{t("confirmPassword2FA")}</Form.Label>
            <Form.Control
              type="password"
              placeholder={t("password")}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoFocus
              disabled={loading}
            />
          </Form.Group>

          <div className="d-flex justify-content-end gap-2">
            <Button variant="secondary" onClick={handleClose} disabled={loading}>
              {t("cancel")}
            </Button>
            <Button
              variant="danger"
              type="submit"
              disabled={!password || loading}
            >
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  {t("loading")}
                </>
              ) : (
                t("disable2FA")
              )}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default TwoFADisableModal;
